import { useQuery, QueryClient, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Module } from "../pages/Profile/hooks/useUsers";

interface UserInfo {
  id?: string;
  samId?: string;
  email: string;
  accountType?: string;
  role?: string;
  modules: Module[];
  username?: string;
  status?: string;
  // Legacy fields for backward compatibility
  type?: string;
  designatedOperator?: string;
  userType?: string;
}

const fetchUserInfo = async (): Promise<UserInfo> => {
  try {
    const response = await axios.get('/user');
    return response.data;
  } catch (error) {
    console.error(`[useUser] Error fetching user info:`, error);
    throw error;
  }
};

// User query key for consistent reference
export const userQueryKey = ['user'];

// Hook to access user data with refetch capability
// IMPORTANT: Only UserInformationProvider should fetch (enabled=true)
// All other components should use enabled=false to read from cache only
// React Query will handle cache/stale logic automatically when enabled=true
export function useUser(enabled: boolean = false) {
  const queryClient = useQueryClient();

  const query = useQuery<UserInfo>({
    queryKey: userQueryKey,
    queryFn: fetchUserInfo,
    enabled, // Only fetch when explicitly enabled (UserInformationProvider)
    // React Query will use cached data if fresh (within staleTime) even when enabled=true
    // Reduce retries - axios interceptor and ProtectedRoute handle retries
    // Only retry on network errors, not 401 (auth) errors
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors - let axios interceptor handle auth retries
      if (error?.response?.status === 401) {
        return false;
      }
      // Retry up to 1 time for other errors (network issues, etc.)
      return failureCount < 1;
    },
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes (prevents refetch if component remounts)
    refetchOnMount: false, // Don't refetch when component mounts if data is fresh (within staleTime)
    refetchOnWindowFocus: false, // Already disabled globally, but explicit here
    refetchOnReconnect: false, // Don't refetch on reconnect if data is fresh
    // Structural sharing ensures React Query properly deduplicates and shares data
    structuralSharing: true,
    // Network mode: 'online' ensures requests are only made when online
    // This helps with deduplication by preventing unnecessary requests
    networkMode: 'online',
  });

  // Function to force refresh user data
  const refreshUserData = () => {
    return queryClient.invalidateQueries({ queryKey: userQueryKey });
  };

  return {
    ...query,
    refreshUserData,
  };
}

// Utility function to invalidate user data from anywhere
export function invalidateUserData(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: userQueryKey });
}